import { Chart } from "primereact/chart";

export const STATUS_COLORS = ["#b45309", "#0284c7", "#7c3aed", "#059669", "#64748b"];
const palette = ["#0284c7", "#6366f1", "#0d9488", "#9333ea", "#b45309"];

export function MetricCard({ label, value, description, icon: Icon, accent = false }) {
  return (
    <article className={`min-w-0 rounded-2xl border p-3 sm:p-5 shadow-sm ${accent ? "border-sky-700 bg-sky-800 text-white" : "border-slate-200 bg-white text-slate-800"}`}>
      <div className="flex items-start justify-between gap-2">
        <p className={`min-w-0 break-words text-[11px] sm:text-xs font-semibold ${accent ? "text-sky-100" : "text-slate-500"}`}>{label}</p>
        {Icon && <Icon size={21} className={`h-4 w-4 sm:h-5 sm:w-5 shrink-0 ${accent ? "text-sky-200" : "text-sky-600"}`} />}
      </div>
      <p className="mt-2 sm:mt-3 break-words text-2xl sm:text-3xl font-bold tracking-tight tabular-nums">{value}</p>
      <p className={`mt-1.5 sm:mt-2 text-[10px] sm:text-xs leading-relaxed ${accent ? "text-sky-100" : "text-slate-500"}`}>{description}</p>
    </article>
  );
}

export function ChartCard({ title, subtitle, type = "bar", labels, datasets, horizontal = false, stacked = false }) {
  const hasData = datasets.some((dataset) => dataset.data.some((value) => value > 0));
  const height = horizontal ? Math.max(260, labels.length * 38 + 65) : 290;
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? false : { duration: 450 },
    indexAxis: horizontal ? "y" : "x",
    cutout: type === "doughnut" ? "72%" : undefined,
    interaction: { mode: type === "line" ? "index" : "nearest", intersect: false },
    plugins: {
      legend: { display: type === "doughnut" || datasets.length > 1, position: "bottom", labels: { usePointStyle: true, boxWidth: 8, padding: 18, color: "#475569" } },
      tooltip: { backgroundColor: "#0f172a", padding: 12, cornerRadius: 10 },
    },
    ...(type !== "doughnut" && { scales: {
      x: { stacked, grid: { display: horizontal, color: "#f1f5f9" }, border: { display: false }, ticks: { color: "#64748b", ...(horizontal && { precision: 0 }) }, ...(horizontal && { beginAtZero: true }) },
      y: { stacked, grid: { display: !horizontal, color: "#f1f5f9" }, border: { display: false }, ticks: { color: "#64748b", ...(!horizontal && { precision: 0 }), ...(horizontal && { callback: function(value) { const label = this.getLabelForValue(value); return label.length > 26 ? `${label.slice(0, 25)}…` : label; } }) }, ...(!horizontal && { beginAtZero: true }) },
    } }),
  };
  return (
    <section className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
      <h2 className="text-base font-bold text-slate-800">{title}</h2>
      <p className="mb-5 mt-1 text-xs leading-relaxed text-slate-500">{subtitle}</p>
      {hasData ? <>
        <div className="max-h-[440px] overflow-y-auto">
          <Chart type={type} data={{ labels, datasets }} options={options} style={{ height }} ariaLabel={title} />
        </div>
        <details className="mt-4 text-xs text-slate-500">
          <summary className="cursor-pointer rounded py-1 focus-visible:outline-sky-600">View exact counts</summary>
          <div className="mt-2 max-h-64 overflow-auto">
            <table className="w-full text-left"><caption className="sr-only">{title}</caption>
              <thead><tr><th className="p-2">Category</th>{datasets.map((dataset) => <th className="p-2" key={dataset.label}>{dataset.label}</th>)}</tr></thead>
              <tbody>{labels.map((label, index) => <tr key={`${label}-${index}`} className="border-t border-slate-100"><th className="p-2 font-medium">{label}</th>{datasets.map((dataset) => <td className="p-2 tabular-nums" key={dataset.label}>{dataset.data[index]}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </details>
      </> : <div className="flex h-64 items-center justify-center rounded-xl bg-slate-50 text-sm text-slate-500">No tickets for these filters.</div>}
    </section>
  );
}

export function CountChart({ title, subtitle, rows, label = "Tickets", color = palette[0], donut = false }) {
  return <ChartCard title={title} subtitle={subtitle} type={donut ? "doughnut" : "bar"} horizontal={!donut}
    labels={rows.map((row) => row.label)} datasets={[{ label, data: rows.map((row) => row.count), backgroundColor: donut ? rows.map((_, index) => palette[index % palette.length]) : color, borderRadius: 6, maxBarThickness: 22, borderWidth: 0 }]} />;
}
